import { UserPrefs } from "./UserPrefs.js";
import { BuildSettings, OutputConfig } from "./OutputConfig.js";
import { Mixer } from "./Mixer.js";
import { Dispatcher } from "./Dispatcher.js";
import { Conference } from "./Conference/Conference.js";
import { Logger } from "./Logger.js";

import { get } from "svelte/store";

import {
    device_permissions,
    avail_devices,
    selected_devices,
    getSelectedDeviceMuted,
    setSelectedDevice,
    getSelectedDevice
} from "./stores.js";

// -- Chrome on Android's synthetic mic list (CommunicationDeviceSelector.DEVICE_NAMES, never localized)
const ANDROID_BUILT_IN_MICS = new Set(["Speakerphone", "Headset earpiece"]);
const ANDROID_HEADSET_MICS = new Set(["Wired headset", "Bluetooth headset", "USB audio"]);

/*
 Devices is a desired-state reconciler. What the user wants lives in two places:

   - the selected_devices store: WHICH device each kind should use (label/id) and whether it
     is muted -- the UI binds straight to this, so "toggle" always means "the opposite of
     what the UI shows", never "the opposite of whatever the hardware reports";
   - _want_open / _publish below: whether a kind should hold a live base track at all and
     whether tracks belong on the conference;
   - the device_permissions store: the app's permission view per kind, recorded ONLY
     through _recordPermission (from the initial query, permissions.onchange, a successful
     GUM open -- the only grant signal that exists on Safari -- or a refused GUM open,
     which records "denied" for the kinds the refusal can be attributed to, even when the
     platform still says "prompt" for them, e.g. a dismissed prompt: either way the open
     was refused and only requestPermissions() re-asks).

 Setters (toggleInputMute, selectInputDevice, _recordPermission, prepareConferenceTracks)
 only write desired state and queue reconcile(). reconcile() is the single place that reads
 ACTUAL state (GUM tracks, the conference's local tracks, jitsi mute) and closes the gap:
 close what shouldn't be open, open what should, publish what's missing, apply mute. Actual
 state writes back into desired in exactly one case -- an open that failed because the
 device doesn't exist drops the desire (_want_open=false) so we don't retry forever.
*/
export const Devices = {

    MUTE_LABEL: "Mute",

    TALENT_AUDIO_PROCESSING: {
        noiseSuppression: false,
        autoGainControl: false,
        echoCancellation: false,
    },

    CONF_RESOLUTION: {
        width: {ideal: 640, max: 960},
        height: {ideal: 360, max: 540},
    },

    stream: null,

    tracks: {
        audio: {id: null, track: null},
        video: {id: null, track: null},
    },

    self_type: "",

    // -- DESIRED STATE (see module comment) -- //
    _want_open: { audio: false, video: false },
    _publish: false,

    // -- PUBLIC -- //
    async init(self_type){

        this.self_type = self_type;

        if (OutputConfig.session.disable_conf)
            return;

        await this._queryPermissions(UserPrefs.prefs).catch(e => console.log(e));
    },

    // -- The start panel's permissions retry. Re-runs the query: fresh verdicts re-arm
    // -- whatever the platform still allows, and the reconcile's open attempt re-raises the
    // -- browser prompt -- which un-sticks a dismissed prompt (recorded "denied" here, but
    // -- still "prompt" to the platform). A hard block stays denied -- browsers never
    // -- re-prompt those -- until the user flips it in the browser's site settings.
    requestPermissions(){
        // -- prefs deliberately withheld: the pref'd camera mute is a page-load default, and
        // -- re-applying it here would re-mute a camera the user has since unmuted
        return this._queryPermissions(null);
    },

    // -- live PermissionStatus per kind, held so a re-query can detach the old onchange
    _perm_status: { camera: null, microphone: null },

    // -- Read the platform verdict for each kind, record it, and kick one reconcile. The
    // -- reconcile's open attempt is what raises the browser prompt for "prompt" kinds.
    async _queryPermissions(prefs){

        let any_openable = false;

        for (const device_type of ["camera", "microphone"]){

            // -- camera/microphone are not queryable everywhere (older Safari throws): treat
            // -- that as "prompt" -- the open attempt below settles the real verdict via GUM
            let result = null;
            try { result = await navigator.permissions.query({ name: device_type }); }
            catch (e) {}

            const state = result?.state ?? "prompt";

            // -- a camera pref'd as muted loads muted (no open, light stays off) -- but only
            // -- when already granted: a "prompt" still has to open to obtain permission.
            // -- Must land before the verdict is recorded, which reads the muted store.
            const load_camera_muted = device_type === "camera"
                && state === "granted"
                && prefs?.devices?.camera?.muted === true;

            if (load_camera_muted)
                setSelectedDevice("camera", { muted: true });

            this._recordPermission(device_type, state);

            if (state !== "denied")
                any_openable = true;

            if (this._perm_status[device_type])
                this._perm_status[device_type].onchange = null;
            this._perm_status[device_type] = result;

            // -- This does not fire on Safari FYI.
            if (result) result.onchange = () => {
                console.log("Permissions Changed: ", device_type, result.state);
                this._recordPermission(device_type, result.state);
                this.reconcile({ enumerate: true });
            };
        }

        if (any_openable)
            await this.reconcile({ enumerate: true });
    },

    // -- THE permission writer: every verdict (query, onchange, GUM grant) lands here, in the
    // -- one store. granted/prompt means the kind should open -- except a camera whose
    // -- selection is already muted: that one stays closed until the user unmutes it.
    _recordPermission(device_type, state){

        // -- ...unless the mute is what a denial left behind. Coming away from "denied" the
        // -- person has just gone to the browser and enabled the device, so open it: a mute
        // -- carried in from prefs (or from before the denial) would otherwise leave the
        // -- camera dark and the menu on "Mute" immediately after they granted it. Pre-join
        // -- only -- in a live room a permission flip must never switch someone's camera on.
        const leaving_denial = state !== "denied" && this._permState(device_type) === "denied";
        if (leaving_denial && device_type === "camera" && !this._publish && getSelectedDeviceMuted("camera"))
            setSelectedDevice("camera", { muted: false });

        device_permissions.update(perms => ({ ...perms, [device_type]: state }));

        const kind = this.deviceToGumType(device_type);

        if (state === "denied")
            this._want_open[kind] = false;
        else
            this._want_open[kind] = !(device_type === "camera" && getSelectedDeviceMuted("camera"));
    },

    _permState(device_type){
        return get(device_permissions)[device_type];
    },

    // -- the platform's LIVE verdict for a kind (PermissionStatus.state tracks changes),
    // -- distinct from our recorded view above; null where the kind isn't queryable
    _permStatusState(device_type){
        return this._perm_status[device_type]?.state ?? null;
    },

    // -- Toolbar mute toggle: flip the DESIRED mute (the opposite of what the UI shows) and
    // -- reconcile. Whether that means a jitsi mute, closing the camera, or re-opening it is
    // -- reconcile's problem, not the caller's.
    toggleInputMute(device_type){

        const muted = !getSelectedDeviceMuted(device_type);

        if (this.self_type === "talent" && device_type === "microphone"){
            Dispatcher.toggleDTSMute(muted);
            setSelectedDevice(device_type, {muted});
            return Promise.resolve();
        }

        setSelectedDevice(device_type, {muted});

        // -- unmuting re-arms the desire to be open: the kind may have been dropped by a
        // -- denial or a failed open, and "unmute" is the user asking for it again
        const kind = this.deviceToGumType(device_type);
        if (device_type === "camera")
            this._want_open.video = !muted && this._permState("camera") !== "denied";
        else if (!muted && this._permState(device_type) !== "denied")
            this._want_open[kind] = true;

        return this.reconcile();
    },

    // -- Start-panel picker selection; label may be MUTE_LABEL (camera only). Picking a real
    // -- camera while muted unmutes it; picking a mic never touches its mute.
    selectInputDevice(device_type, label){

        if (label === this.MUTE_LABEL){
            if (getSelectedDeviceMuted(device_type))
                return Promise.resolve();
            return this.toggleInputMute(device_type);
        }

        const device = this.getDeviceByLabel(device_type, label);
        setSelectedDevice(device_type, { label: label, id: device?.deviceId ?? null });

        if (device_type === "camera" && getSelectedDeviceMuted("camera"))
            setSelectedDevice("camera", { muted: false });

        const kind = this.deviceToGumType(device_type);
        if (this._permState(device_type) !== "denied")
            this._want_open[kind] = true;

        return this.reconcile();
    },

    
    prepareConferenceTracks(){
        this._publish = true;
        return this.reconcile();
    },

    // -- called from `devicechange` event. Resets avail devices if they are different and does devices substitution if our current device has dropped
    async reconcileDevicesAfterChange(){
        await this.setAvailDevices();
        return this.reconcile();
    },

    // -- request queue since they are async and we don't await anything in our code
    reconcile(opts = {}){
        const run = this._chain.then(() => this._reconcile(opts));
        this._chain = run.catch(() => {});
        return run;
    },

    _chain: Promise.resolve(),

    // -- called when devices get added
    async setAvailDevices(devices = null){
        const d = devices ? devices : await this._enumerateDevices();
        if (!this._devicesEqual(get(avail_devices), d))
            avail_devices.set(d);
        return d;
    },

    // -- INTERNAL: the reconcile pass -- //

    // -- pl-ws refused this tab as a second eng: it can never join, so close both devices for good.
    // -- Queued behind any in-flight pass, and every later pass (devicechange) just re-closes
    release(){
        this._released = true;
        return this.reconcile();
    },

    _released: false,

    async _reconcile({ enumerate = false } = {}){

        if (this._released){
            for (const kind of ["audio", "video"])
                if (this.tracks[kind].track) await this._closeTrack(kind);
            return;
        }

        let devices = enumerate ? await this._enumerateDevices() : get(avail_devices);

        await this._closeUnwanted();

        const opened = await this._openWanted(devices);

        await this._publishOpen(opened);

        await this._applyConferenceMutes();

        if (enumerate){
            // -- again: our permissions may have changed and Safari has no permissions.onchange,
            // -- so labels can appear only after the GUM grant. setAvailDevices also wakes
            // -- OutputDevices, which fills/validates the output selections.
            devices = await this._enumerateDevices();
            this.setAvailDevices(devices);

            // -- name any still-unset input selection from prefs (e.g. a camera that loaded
            // -- muted was never opened, so nothing else named it)
            this._setInputSelectionsFromPrefs(UserPrefs.prefs, devices);
        }

        if (opened.audio && this.tracks.audio.track)
            Mixer.addConfSource(this.stream, "self");
    },

    // -- close what we shouldn't hold. A revoked permission closes everything (conference
    // -- included); a pre-join camera mute closes just the base device -- the light goes off,
    // -- same end state as a camera whose pref loads as "Mute". A post-join camera mute is NOT
    // -- a close: the published wrapper stays and gets jitsi-muted in the mute pass.
    async _closeUnwanted(){

        for (const kind of ["audio", "video"]){

            const device_type = this.gumToDeviceType(kind);
            const has_wrapper = this._conferenceEnabled() && Conference.hasLocalTrack(kind);
            const has_base = !!this.tracks[kind].track;

            if (this._permState(device_type) === "denied"){
                if (has_base || has_wrapper)
                    await this._closeTrack(kind);
                continue;
            }

            if (kind === "video" && !this._want_open.video && has_base && !has_wrapper)
                await this._closeTrack(kind);
        }
    },

    // -- open (or re-open) base tracks so each wanted kind sits on its desired device
    async _openWanted(devices){

        const to_open = {};

        for (const kind of ["audio", "video"]){

            if (!this._want_open[kind])
                continue;

            const device_type = this.gumToDeviceType(kind);
            const muted = getSelectedDeviceMuted(device_type);

            if (kind === "video" && muted)
                continue; // -- a muted camera is closed by policy (or wrapper-muted post-join)

            const track = this.tracks[kind].track;
            const live = !!track && track.readyState !== "ended";
            const selected = getSelectedDevice(device_type);
            const target = (selected?.label && this.getDeviceByLabel(device_type, selected.label, devices))
                        || (selected?.id    && this.getDeviceByID(device_type, selected.id, devices))
                        || null;

            // -- an explicit switch to a device that exists always opens (mute doesn't bar a
            // -- mic switch -- the mute rides onto the new track at publish/replace)
            if (target && target.deviceId !== this.tracks[kind].id)
                to_open[kind] = true;

            // -- nothing live: the initial open, an unmute re-open, or a device that died.
            // -- Two carve-outs: a published camera whose device is still present re-acquires
            // -- through lib-jitsi-meet's unmute (mute pass) -- we never re-GUM underneath its
            // -- wrapper -- but a device that actually left the list is repaired even under a
            // -- wrapper (open + replace). And a muted mic is left dead rather than silently
            // -- re-opened on a device the user never picked; unmuting or selecting repairs it.
            else if (!live && !muted){
                if (kind === "video" && target && this._conferenceEnabled() && Conference.hasLocalTrack(kind))
                    continue;
                to_open[kind] = true;
            }

            // -- live track whose device left the list (e.g. BT put away): fall back
            else if (live && !target && (selected?.label || selected?.id) && !muted){
                console.warn(`[PL-Devices] Selected ${device_type} gone -- re-opening on the default device`);
                to_open[kind] = true;
            }
        }

        if (Object.keys(to_open).length === 0)
            return {};

        const attempted = Object.keys(to_open);
        const constraints = this._constraintsFor(to_open, devices);

        // -- the selection label each kind is being opened FOR. gUM can sit behind a prompt for
        // -- seconds; if the user re-selects meanwhile, the newer desired label must survive
        // -- this pass (the queued pass switches to it) instead of being clobbered below.
        const requested = {};
        for (const kind of attempted)
            requested[kind] = getSelectedDevice(this.gumToDeviceType(kind))?.label ?? null;

        // -- _acquireTracks prunes to_open for any kind with no usable device
        let new_tracks;
        try {
            new_tracks = await this._acquireTracks(constraints, to_open);
        }
        catch (e){
            // -- a refusal (including a dismissed prompt) drops the desire for everything
            // -- attempted -- nothing retries, or re-raises the browser prompt, on its own --
            // -- and records "denied" so the start panel surfaces its permissions error.
            // -- GUM is atomic (one refused kind rejects the whole call), so the verdict only
            // -- lands where it can be attributed: a kind the platform still reports granted
            // -- was NOT the one refused (its prompt was never shown -- the other kind's
            // -- prompt was dismissed, or the other kind is blocked at the OS level). Such a
            // -- kind is re-wanted instead: opening it alone cannot prompt, so the queued
            // -- pass recovers it. Kinds pruned earlier (missing device) weren't refused.
            if (this._isPermissionDenial(e)){

                const kinds = Object.keys(to_open);
                const refused = kinds.filter(kind => this._permStatusState(this.gumToDeviceType(kind)) !== "granted");

                for (const kind of attempted)
                    this._want_open[kind] = false;

                for (const kind of refused)
                    this._recordPermission(this.gumToDeviceType(kind), "denied");

                // -- refused empty (all platform-granted, e.g. an OS-level block with the site
                // -- allowed) means we can't attribute anything: record nothing, retry nothing
                if (refused.length > 0 && refused.length < kinds.length){
                    for (const kind of kinds)
                        if (!refused.includes(kind)) this._want_open[kind] = true;
                    this.reconcile();
                }
            }
            throw e;
        }

        const opened = {};

        for (const new_track of new_tracks){

            const kind = new_track.kind;

            if (!this.stream) this.stream = new MediaStream();
            const old_track = this.stream.getTracks().find(t => t.kind === kind);
            if (old_track){
                old_track.stop();
                this.stream.removeTrack(old_track);
            }
            this.stream.addTrack(new_track);

            const id = this._inferDeviceId(new_track, devices, null);
            this.tracks[kind] = { id: id, track: new_track };
            opened[kind] = true;

            const device_type = this.gumToDeviceType(kind);

            // -- actual state backfills desired: an open with no explicit device resolves to a
            // -- concrete one, and that becomes the selection the UI shows. Unless the user
            // -- re-selected while gUM was in flight -- then only the track lands, and the
            // -- queued pass switches to the newer choice.
            if ((getSelectedDevice(device_type)?.label ?? null) === requested[kind])
                setSelectedDevice(device_type, { label: new_track.label, id: id, track: new_track });
            else
                setSelectedDevice(device_type, { track: new_track });

            this._recordGrant(kind, new_track.label);
        }

        // -- a pruned kind (no usable device). If nothing was open, stop wanting it: plugging a
        // -- device in later doesn't auto-open, the user picks it from the (refreshed) list. If
        // -- a previous track survives (a switch to a busy/vanished device failed), keep wanting
        // -- it and snap the selection back to what is actually running -- otherwise a later
        // -- pass would read the surviving track as unwanted and close a working device.
        for (const kind of attempted){
            if (kind in to_open)
                continue;

            const existing = this.tracks[kind].track;
            if (!existing){
                this._want_open[kind] = false;
                continue;
            }

            const device_type = this.gumToDeviceType(kind);
            if ((getSelectedDevice(device_type)?.label ?? null) === requested[kind])
                setSelectedDevice(device_type, { label: existing.label, id: this.tracks[kind].id });
        }

        return opened;
    },

    // -- publish any new tracks to the conference if we have joined
    async _publishOpen(opened){

        if (!this._publish || !this._conferenceEnabled())
            return;

        const request = {};

        for (const kind of ["audio", "video"]){
            const track = this.tracks[kind].track;
            if (!track) continue;
            if (opened[kind] || !Conference.hasLocalTrack(kind))
                request[kind] = { track: track };
        }

        if (Object.keys(request).length > 0)
            await Conference.addLocalTracks(request, this.stream);
    },

    // -- make every published wrapper follow the desired mute. For video, lib-jitsi-meet's
    // -- unmute re-acquires the underlying device -- pick the fresh MediaStreamTrack up as our
    // -- base track so previews/tiles render the live one.
    async _applyConferenceMutes(){

        if (!this._conferenceEnabled())
            return;

        for (const kind of ["audio", "video"]){

            if (!Conference.hasLocalTrack(kind))
                continue;

            const muted = getSelectedDeviceMuted(this.gumToDeviceType(kind));
            const ok = await Conference.applyLocalMute(kind, muted);

            if (ok && kind === "video" && !muted){
                const live = Conference.getLocalMediaStreamTrack(kind);
                if (live && live !== this.tracks.video.track){
                    this.tracks.video = { id: this.tracks.video.id, track: live };
                    setSelectedDevice("camera", { track: live });
                }
            }
        }
    },

    _conferenceEnabled(){
        return this.self_type !== "talent" && !OutputConfig.session?.disable_conf;
    },

    deviceToGumType(device_type){
        if (device_type === "microphone") return "audio";
        if (device_type === "camera") return "video";
        return device_type;
    },

    gumToDeviceType(gum_type){
        if (gum_type === "audio") return "microphone";
        if (gum_type === "video") return "camera";
        return gum_type;
    },

    // -- constraints for the kinds being opened: the selection's label, else the user-pref
    // -- label, else let GUM decide
    _constraintsFor(kinds, devices){

        const constraints = {};

        for (const kind in kinds){

            const device_type = this.gumToDeviceType(kind);

            let label = getSelectedDevice(device_type)?.label;
            if (!label)
                label = UserPrefs.prefs?.devices?.[device_type]?.label ?? null;

            const id = label ? this.getDeviceByLabel(device_type, label, devices)?.deviceId : null;
            constraints[kind] = id ? { deviceId: { exact: id } } : true;

            if (kind === "audio" && this.self_type === "talent")
                constraints[kind] = { ...constraints[kind], ...this.TALENT_AUDIO_PROCESSING };

            // -- Android: a capture stream with AEC puts Chrome in MODE_IN_COMMUNICATION, which
            // -- lands the AudioContext output on the voice-call path (OEM DSP + BT SCO)
            if (kind === "audio" && OutputConfig.session?.browser?.isAndroid)
                constraints[kind] = { ...constraints[kind], echoCancellation: false };

            // -- iOS, same story: with AEC, WebKit captures through Apple's voice processor and plays our
            // -- program through it too (louder, gain ramps, crackle on iPad Pro speaker)
            if (kind === "audio" && OutputConfig.session?.browser?.isIOS && !BuildSettings.IOS_MIC_VOICE_PROCESSING)
                constraints[kind] = { ...constraints[kind], echoCancellation: false };

            if (kind === "video")
                constraints[kind] = { ...constraints[kind], ...this.CONF_RESOLUTION };
        }

        return constraints;
    },

    // -- iOS/Android: is this mic selection the device's own? With AEC off (above) it hears the speaker.
    // -- iOS labels it after the device ("iPhone Microphone", localized around the name). Chrome on
    // -- Android lists fixed English names, and its "default" picks a headset whenever one is listed
    isBuiltInMobileMic(selection, mics = [], browser = OutputConfig.session?.browser){

        const label = selection?.label ?? "";

        if (browser?.isIOS)
            return /\b(?:iPhone|iPad|iPod)\b/.test(label);

        if (browser?.isAndroid){
            if (selection?.id === "default")
                return !mics.some(d => ANDROID_HEADSET_MICS.has(d.label));
            return ANDROID_BUILT_IN_MICS.has(label);
        }

        return false;
    },

    // -- Acquire the requested tracks, preferring a single combined getUserMedia so the
    // -- user sees one permission prompt. getUserMedia is atomic: if any requested kind has
    // -- no usable device the whole call rejects, which would otherwise take down the kinds
    // -- that *are* present (e.g. no camera also losing a working mic). On a missing-device
    // -- rejection we fall back to probing each kind on its own and prune request[type] for
    // -- whichever has no device. Permission denials are not "missing device" -- we rethrow
    // -- so the caller of this pass sees them and downstream is skipped.
    async _acquireTracks(constraints, request){

        try {
            const stream = await Default_GUM.call(navigator.mediaDevices, constraints);
            return stream.getTracks();
        }
        catch (e){
            // -- the only denial signal that exists on Safari (permissions.onchange is dead
            // -- there); a big slice of the pre-join dropoff was invisible without this
            if (this._isPermissionDenial(e)){
                Logger.log("Devices", "warn", "Device Permission Denied", {kinds: Object.keys(constraints), error: e.name});
                throw e;
            }
            if (!this._isMissingDeviceError(e)){
                Logger.log("Devices", "warn", "Device Open Failed", {kinds: Object.keys(constraints), error: e.name});
                throw e;
            }
            console.warn("[PL-Devices] Combined getUserMedia failed -- probing kinds individually:", e.name);
        }

        const tracks = [];

        for (const type of Object.keys(constraints)){
            try {
                const stream = await Default_GUM.call(navigator.mediaDevices, { [type]: constraints[type] });
                tracks.push(...stream.getTracks());
            }
            catch (e){
                if (this._isPermissionDenial(e)){
                    Logger.log("Devices", "warn", "Device Permission Denied", {kinds: [type], error: e.name});
                    throw e;
                }
                if (!this._isMissingDeviceError(e)){
                    Logger.log("Devices", "warn", "Device Open Failed", {kinds: [type], error: e.name});
                    throw e;
                }
                Logger.log("Devices", "info", "Device Unavailable", {kind: this.gumToDeviceType(type), error: e.name});
                console.warn(`[PL-Devices] No usable ${this.gumToDeviceType(type)} -- dropping from request:`, e.name);
                delete request[type];
            }
        }

        return tracks;
    },

    _isPermissionDenial(error){
        return error?.name === "NotAllowedError"
            || error?.name === "SecurityError"
            || error?.name === "PermissionDeniedError"; // -- legacy alias
    },

    // -- device-absent / unusable: no such device, a pinned deviceId that is gone, or a
    // -- device that exists but can't be opened (in use / hardware error). All mean we can't
    // -- produce this track, so the caller prunes it from the request.
    _isMissingDeviceError(error){
        return error?.name === "NotFoundError"
            || error?.name === "DevicesNotFoundError"  // -- legacy alias
            || error?.name === "OverconstrainedError"  // -- exact deviceId no longer present
            || error?.name === "NotReadableError";     // -- device present but unusable
    },

    // -- Tear down a live track (e.g. its camera/mic permission was revoked): drop it from the
    // -- conference, stop the underlying GUM track, and clear the selection + readiness so the
    // -- UI reflects that the device is gone. Safe to call when nothing is open.
    async _closeTrack(type){

        const device_type = this.gumToDeviceType(type);

        // -- remove from the conference (no-op for talent / when no jitsi track is present)
        if (this.self_type !== "talent")
            await Conference.removeLocalTrack(type).catch(e => console.error(e));

        // -- the conference may wrap a clone, so the base GUM track has to be stopped separately
        const base = this.tracks[type]?.track;
        if (base){
            base.stop();
            this.stream?.removeTrack(base);
        }
        this.tracks[type] = { id: null, track: null };

        // -- drop the live track from the selection (keep label/id so a re-grant can restore
        // -- it); readiness is derived from the selection, so it follows on its own
        setSelectedDevice(device_type, { track: null });
    },

    // -- once-per-kind-per-page guard for the grant logs (reconcile runs on every device
    // -- change; only the first successful open per kind is journey-relevant)
    _perm_logged: {},

    // -- a successful GUM open IS the grant. Needed because on Safari permissions.onchange
    // -- never fires, so a grant that arrives via the GUM prompt only gets recorded (and its
    // -- journey log written) here.
    _recordGrant(kind, label){

        this._recordPermission(this.gumToDeviceType(kind), "granted");

        if (!this._perm_logged[kind]){
            this._perm_logged[kind] = true;
            Logger.log("Devices", "info", "Device Permission Granted", {kind: this.gumToDeviceType(kind), label: label});
        }
    },

    _printSelectedDevices(){
        const sd = get(selected_devices);
        console.warn("[PL-Devices] Current Selected Devices: ", get(sd.camera.track), get(sd.microphone.track), get(sd.camera.muted), get(sd.microphone.muted),);
    },

    // -- structural equality of two enumerated device maps, by (deviceId, label) per kind and
    // -- order-independent (Safari can reorder). Catches add/remove and label changes (e.g. the
    // -- "default" output being relabeled) while treating a mere track re-open as "no change".
    _devicesEqual(a, b){
        for (const kind of ["camera", "microphone", "speaker"]){
            const la = a?.[kind] ?? [];
            const lb = b?.[kind] ?? [];
            if (la.length !== lb.length)
                return false;
            for (const d of la){
                if (!lb.some(e => e.deviceId === d.deviceId && e.label === d.label))
                    return false;
            }
        }
        return true;
    },

    deviceToAvailableDeviceType(device_type){
        if (device_type === "stream") return "speaker";
        return device_type;
    },

    getDeviceByLabel(type, label, devices = get(avail_devices)){

        const device_type = this.deviceToAvailableDeviceType(type);

        for (const d of devices[device_type] ?? []){
            if (d.label === label)
                return d;
        }

        return null;
    },

    getDeviceByID(type, id, devices = get(avail_devices)){

        const device_type = this.deviceToAvailableDeviceType(type);

        for (const d of devices[device_type] ?? []){
            if (d.deviceId === id || d.id === id)
                return d;
        }

        return null
    },

    _inferDeviceId(track, devices, device_id = null) {

        if (device_id) return device_id;
        let id = track.getSettings?.().deviceId;
        if (id) return id;

        const norm_type = track.kind === "audio" ? "microphone" : "camera";
        const matches = devices[norm_type]?.filter(device => device.label === track.label);

        if (matches?.length === 1)
          return matches[0].deviceId;

        return track.id;
    },

    // -- This is AI messy but MAYBE ok for now -- ?
    async _enumerateDevices(){

        const devices = await navigator.mediaDevices.enumerateDevices();
        const newDevices = {
            camera: [],
            microphone: [],
            speaker: [],
        };

        devices.forEach(device => {
            // -- do we still want this?
            if (device.deviceId === '')
                return;
            if (device.kind === 'videoinput')
                newDevices.camera.push(device);
            else if (device.kind === 'audioinput')
                newDevices.microphone.push(device);
            else if (device.kind === 'audiooutput')
                newDevices.speaker.push(device);
        });

        return newDevices;
    },

    // -- Input Selection From Prefs -- //
    // -- name any camera/microphone selection an open track hasn't already named (a camera that
    // -- loaded muted was never opened): the pref'd device if present, else the default. The
    // -- speaker/stream equivalents live in OutputDevices.
    _setInputSelectionsFromPrefs(prefs, devices){

        const pref_devices = prefs?.devices ?? {};

        selected_devices.update(_selected_devices => {

            const update = {..._selected_devices};

            // -- we might update this policy if we want to implement forced non BlueTooth mic in certain situations. This might be the place to do that.
            for (const type of ["camera", "microphone"]){

                if (!update[type] || update[type].label || update[type].id)
                    continue;

                const label = pref_devices[type]?.label;
                const pref_device = label
                    ? (devices[type] ?? []).find(device => device.label === label) ?? null
                    : null;

                const device = pref_device ?? this.getDefaultDevice(devices[type] ?? []);
                if (!device)
                    continue;

                update[type] = {
                    ...update[type],
                    label: device.label ?? "",
                    id: device.deviceId ?? null
                };
            }

            return update;
        });

    },

    getDefaultDevice(devices){
        return devices.find(d => d.deviceId?.toLowerCase().includes("default") || d.label?.toLowerCase().includes("default"))
            ?? devices[0]
            ?? null;
    },

}
